import React, { useEffect, useState, Suspense } from 'react';
import Modal from '../Modal';
import { useTranslation } from 'react-i18next';

// lazy imports — these modules will only be fetched when rendered
const Welcome = React.lazy(() => import('./Welcome'));
const Login = React.lazy(() => import('./Login'));
const Register = React.lazy(() => import('./Register'));
const DealerRegister = React.lazy(() => import('./DealerRegister'));

// small fallback while chunk loads
const SuspenseFallback = () => (
  <div className="w-full flex items-center justify-center py-8">
    <div className="animate-spin rounded-full h-8 w-8 border-4 border-white/30 border-t-white" />
  </div>
);

/* =============================================================================
<AuthModal />
============================================================================= */
const AuthModal = ({ page, onPageOpen }) => {
  const [model, setModal] = useState(!!page);
  const { t } = useTranslation();
  const _toggleModal = () => setModal((prev) => !prev);

  useEffect(() => {
    if (page) {
      setModal(!!page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const description =
    page === t('auth.dealer_register.title')
      ? 'You will register as dealer'
      : page === t('auth.welcome.title')
        ? t('auth.welcome.description')
        : null;

  const _handleClose = () => {
    setModal(false);

    setTimeout(() => {
      onPageOpen(null);
    }, 600);
  };

  const _handlePageSelect = (_page) => {
    _toggleModal();

    setTimeout(() => {
      onPageOpen(_page);
    }, 600);
  };

  // Optional: Prefetch likely next chunks when modal opens (warm the network)
  useEffect(() => {
    if (!model) return;

    // example: if they opened welcome, they might go to Sign in or Sign up next
    if (page === 'WELCOME TO CARSNUS') {
      import('./Login'); // starts fetching Login chunk in background
      import('./Register');
    }

    // if you prefer, prefetch dealer when 'Sign up' is shown
    if (page === 'Sign up') {
      import('./DealerRegister');
    }
    // NOTE: these imports are fire-and-forget — they won't render anything
  }, [model, page]);

  const renderContent = () => {
    // wrap each lazy component in Suspense; single Suspense for all is also fine
    if (page === t('auth.welcome.title')) {
      return (
        <Suspense fallback={<SuspenseFallback />}>
          <Welcome onPageChange={_handlePageSelect} onClose={_handleClose} />
        </Suspense>
      );
    }

    if (page === t('common.signIn')) {
      return (
        <Suspense fallback={<SuspenseFallback />}>
          <Login
            modal={model}
            onPageChange={_handlePageSelect}
            onClose={_handleClose}
          />
        </Suspense>
      );
    }

    if (page === t('auth.welcome.signUpPrivate')) {
      return (
        <Suspense fallback={<SuspenseFallback />}>
          <Register
            modal={model}
            onPageChange={_handlePageSelect}
            onClose={_handleClose}
          />
        </Suspense>
      );
    }

    if (page === t('auth.welcome.signUpDealer')) {
      return (
        <Suspense fallback={<SuspenseFallback />}>
          <DealerRegister
            modal={model}
            onPageChange={_handlePageSelect}
            onClose={_handleClose}
          />
        </Suspense>
      );
    }

    return null;
  };

  return (
    <Modal
      visible={model}
      heading={page}
      onClose={_handleClose}
      closeOnBackPress={false}
      varient={page === 'WELCOME TO CARSNUS' ? 'secondary' : 'primary'}
      description={description}
    >
      {renderContent()}
    </Modal>
  );
};

/* Export
============================================================================= */
export default AuthModal;
